import { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend,
} from 'chart.js';
import {
  AffilmasterApiError,
  type AffiliateCampaignChart,
  type AffiliateMonthBreakdown,
} from '../api/affilmaster';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const CZ_MONTHS = ['leden', 'únor', 'březen', 'duben', 'květen', 'červen', 'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec'];
function monthLabel(ym: string): string {
  const [y, m] = ym.split('-'); const i = Number(m) - 1;
  if (i < 0 || i >= 12) return ym;
  const nm = CZ_MONTHS[i] ?? '';
  return `${nm.charAt(0).toUpperCase()}${nm.slice(1)} ${y}`;
}
function fmtMoney(v: number, currency: string): string {
  const s = new Intl.NumberFormat('cs-CZ').format(Math.round(v || 0));
  return currency === 'EUR' ? `${s} €` : `${s} Kč`;
}
function fmtN(v: number): string { return new Intl.NumberFormat('cs-CZ').format(Math.round(v || 0)); }
function roiPct(ngr: number, comm: number): string {
  if (!comm) return '-';
  return `${new Intl.NumberFormat('cs-CZ').format(Math.round(((ngr - comm) / comm) * 100))} %`;
}

function Modal({ title, subtitle, onClose, children, wide }: { title: string; subtitle?: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="aff-modal-backdrop" onClick={onClose}>
      <div className={`aff-modal ${wide ? 'aff-modal-wide' : ''}`} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="aff-modal-head">
          <div className="min-w-0">
            <div className="aff-modal-eyebrow">DETAIL KAMPANĚ</div>
            <div className="aff-modal-title">{title}</div>
            {subtitle && <div className="aff-modal-sub">{subtitle}</div>}
          </div>
          <button type="button" className="btn-close" aria-label="Zavřít" onClick={onClose} />
        </div>
        <div className="aff-modal-body">{children}</div>
      </div>
    </div>
  );
}

const METRICS = [
  { key: 'registrations', label: 'Registrace', color: '#0071e3', money: false },
  { key: 'ftd_count', label: 'FTD', color: '#5b3f96', money: false },
  { key: 'deposits_volume', label: 'Vklady', color: '#ff9500', money: true },
  { key: 'ngr_volume', label: 'NGR', color: '#34c759', money: true },
  { key: 'commission', label: 'Provize', color: '#c82b23', money: true },
] as const;

type MetricKey = (typeof METRICS)[number]['key'];
const dayLabel = (ymd: string) => { const [, m, d] = ymd.split('-'); return `${Number(d)}.${Number(m)}`; };

export function CampaignChartModal({ campaignName, fetchChart, onClose }: { campaignName: string; fetchChart: () => Promise<AffiliateCampaignChart>; onClose: () => void }) {
  const [data, setData] = useState<AffiliateCampaignChart | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [active, setActive] = useState<MetricKey[]>(['registrations']); // pořadí: [0]=primární, [1]=čárkovaná
  const [month, setMonth] = useState<string>(''); // '' = podle range; jinak 'YYYY-MM' (denní)
  const [range, setRange] = useState<'3' | '6' | '12' | 'all'>('6');

  useEffect(() => {
    let cancelled = false;
    fetchChart()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Graf se nepodařilo načíst'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currency = data?.currency ?? 'CZK';
  const fmtV = (m: MetricKey, v: number) => (METRICS.find((x) => x.key === m)!.money ? fmtMoney(v, currency) : fmtN(v));

  const monthlyAll = useMemo(() => {
    const map = new Map<string, Record<string, number>>();
    for (const d of data?.daily ?? []) {
      const ym = d.day.slice(0, 7); const o = map.get(ym) ?? {};
      for (const m of METRICS) o[m.key] = (o[m.key] ?? 0) + (d[m.key] as number);
      map.set(ym, o);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [data]);
  const months = useMemo(() => monthlyAll.map(([ym]) => ym).reverse(), [monthlyAll]);

  const toggle = (k: MetricKey) => setActive((cur) => {
    if (cur.includes(k)) { const n = cur.filter((x) => x !== k); return n.length ? n : cur; } // aspoň 1 aktivní
    return cur.length >= 2 ? [cur[1]!, k] : [...cur, k]; // max 2 (primární + čárkovaná)
  });

  const daily = useMemo(() => {
    if (!month) return [] as Array<{ day: string; row?: AffiliateCampaignChart['daily'][number] }>;
    const [y, m] = month.split('-').map(Number);
    const last = new Date(y!, m!, 0).getDate();
    const byDay = new Map((data?.daily ?? []).filter((d) => d.day.slice(0, 7) === month).map((d) => [Number(d.day.slice(8, 10)), d]));
    return Array.from({ length: last }, (_, i) => ({ day: `${month}-${String(i + 1).padStart(2, '0')}`, row: byDay.get(i + 1) }));
  }, [data, month]);
  const monthlyShown = useMemo(() => (range === 'all' ? monthlyAll : monthlyAll.slice(-Number(range))), [monthlyAll, range]);
  const labels = month ? daily.map((d) => dayLabel(d.day)) : monthlyShown.map(([ym]) => monthLabel(ym));
  const datasets = active.slice(0, 2).map((key, i) => {
    const def = METRICS.find((m) => m.key === key)!;
    const vals = month ? daily.map((d) => Math.round((d.row?.[key] as number) ?? 0)) : monthlyShown.map(([, o]) => Math.round(o[key] ?? 0));
    return { label: def.label, data: vals, borderColor: def.color, backgroundColor: `${def.color}22`,
      borderDash: i === 1 ? [6, 4] : [], fill: i === 0, tension: 0.3, pointRadius: 1.5, borderWidth: 2, yAxisID: i === 1 ? 'y2' : 'y' };
  });
  const hasData = labels.length > 0;
  const totals = useMemo(() => (data?.daily ?? []).reduce<Record<MetricKey, number>>((sum, row) => {
    for (const metric of METRICS) sum[metric.key] += Number(row[metric.key] ?? 0);
    return sum;
  }, { registrations: 0, ftd_count: 0, deposits_volume: 0, ngr_volume: 0, commission: 0 }), [data]);
  const options = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { display: active.length > 1, position: 'bottom' as const },
      tooltip: { callbacks: { label: (c: { datasetIndex: number; parsed: { y: number } }) => fmtV(active[c.datasetIndex] ?? active[0]!, c.parsed.y) } },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, position: 'left' as const, ticks: { callback: (v: number | string) => fmtV(active[0]!, Number(v)) } },
      ...(active.length > 1 ? { y2: { beginAtZero: true, position: 'right' as const, grid: { drawOnChartArea: false }, ticks: { callback: (v: number | string) => fmtV(active[1]!, Number(v)) } } } : {}),
    },
  };

  return (
    <Modal title={data?.campaign_name ?? campaignName} subtitle="Vývoj kampaně" onClose={onClose} wide>
      {err && <div className="alert alert-danger mb-0">{err}</div>}
      {!err && !data && <div className="text-muted"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>}
      {data && (
        <>
          <div className="prototype-campaign-controls">
            <div><label>Metriky <small>Vyberte 1–2 metriky</small></label><div className="btn-group btn-group-sm" role="group">
              {METRICS.map((m) => (
                <button key={m.key} type="button"
                  className={`btn btn-outline-secondary ${active.includes(m.key) ? 'active' : ''}`}
                  onClick={() => toggle(m.key)}>{m.label}</button>
              ))}
            </div></div>
            <div className="prototype-campaign-period"><label>Období</label>
              <select className="form-select form-select-sm" style={{ minWidth: 150 }} value={month} onChange={(e) => setMonth(e.target.value)}>
                <option value="">Období: podle range</option>
                {months.map((ym) => <option key={ym} value={ym}>{monthLabel(ym)}</option>)}
              </select>
            </div>
              <div className="btn-group btn-group-sm" role="group">
                {([['3', '3 měs'], ['6', '6 měs'], ['12', '12 měs'], ['all', 'Vše']] as const).map(([r, l]) => (
                  <button key={r} type="button" disabled={!!month}
                    className={`btn btn-outline-secondary ${range === r && !month ? 'active' : ''}`}
                    onClick={() => setRange(r)}>{l}</button>
                ))}
              </div>
          </div>
          <div className="prototype-modal-kpis">
            {METRICS.map((metric) => <div key={metric.key}><span>{metric.label}</span><strong>{fmtV(metric.key, totals[metric.key])}</strong></div>)}
          </div>
          {!hasData
            ? <div className="text-muted py-4 text-center">Žádná data.</div>
            : <div className="prototype-modal-chart"><Line data={{ labels, datasets }} options={options as never} /></div>}
        </>
      )}
    </Modal>
  );
}

export function MonthBreakdownModal({ ym, subtitle, fetchMonth, onClose }: { ym: string; subtitle?: string; fetchMonth: () => Promise<AffiliateMonthBreakdown>; onClose: () => void }) {
  const [data, setData] = useState<AffiliateMonthBreakdown | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchMonth()
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e: unknown) => { if (!cancelled) setErr(e instanceof AffilmasterApiError ? e.message : 'Rozpad se nepodařilo načíst'); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currency = data?.currency ?? 'CZK';
  return (
    <Modal title={`Kampaně · ${monthLabel(ym)}`} subtitle={subtitle ?? 'Rozpad za měsíc'} onClose={onClose} wide>
      {err && <div className="alert alert-danger mb-0">{err}</div>}
      {!err && !data && <div className="text-muted"><span className="spinner-border spinner-border-sm me-2" /> Načítám…</div>}
      {data && (
        <div className="aff-table-wrap">
          <div className="table-responsive-mobile">
          <table className="table table-sm align-middle mb-0 aff-detail-table aff-breakdown-table">
            <thead>
              <tr>
                <th className="ps-2">Kampaň</th><th className="text-end">Registrace</th><th className="text-end">FTD</th>
                <th className="text-end">Vklady</th><th className="text-end">NGR</th><th className="text-end">Odm. RS</th>
                <th className="text-end">Odm. CPA</th><th className="text-end">NGR ROI</th>
              </tr>
            </thead>
            <tbody>
              {data.campaigns.length === 0 && <tr><td colSpan={8} className="text-center text-muted py-3">Žádné kampaně.</td></tr>}
              {data.campaigns.map((c) => (
                <tr key={c.campaign_id}>
                  <td className="ps-2"><strong>{c.campaign_name ?? '-'}</strong></td>
                  <td className="text-end">{fmtN(c.registrations)}</td>
                  <td className="text-end">{fmtN(c.ftd_count)}</td>
                  <td className="text-end">{fmtMoney(c.deposits_volume, currency)}</td>
                  <td className="text-end">{fmtMoney(c.ngr_volume, currency)}</td>
                  <td className="text-end">{fmtMoney(c.commission_rs, currency)}</td>
                  <td className="text-end">{fmtMoney(c.commission_cpa, currency)}</td>
                  <td className="text-end">{roiPct(c.ngr_volume, c.commission)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </Modal>
  );
}
